// Urinalysis Bench — gross appearance, refractometer USG, dipstick reading + diagnosis.
// Purely client-side, single-session. Pitched at Cert IV Veterinary Nursing.
//
// Five staged beats per case (budget = 120):
//   1. Gross appearance   (colour 10 + clarity 10 = 20)
//   2. Refractometer USG  (read value 15 + classify 5 = 20)
//   3. Reagent dipstick   (match pads to the chart, scaled to 30)
//   4. Interpretation     (choose the condition, then a detailed overview · 30)

import { optPts } from "@/data/admission";
export { optPts };

// Approximate urine colours for rendering the specimen.
export const URINE_COLORS = {
  colourless: "#f7f6da",
  "pale-yellow": "#efe6a0",
  yellow: "#eccf38",
  "dark-amber": "#c89009",
  "orange-brown": "#bd5f1c",
  "red-brown": "#8e3a2d",
};

// Reagent-pad reference chart. Colours approximate a generic veterinary dipstick;
// real charts vary by brand. Each analyte lists its levels low→high.
export const PAD_CHART = {
  pH: {
    name: "pH",
    levels: [
      { l: "5.0", c: "#e8643c" },
      { l: "6.0", c: "#e98e3a" },
      { l: "6.5", c: "#e3b24a" },
      { l: "7.0", c: "#cdbf5a" },
      { l: "7.5", c: "#8fae54" },
      { l: "8.0", c: "#4f9a6a" },
      { l: "8.5", c: "#2f7d7a" },
    ],
  },
  protein: {
    name: "Protein",
    levels: [
      { l: "Neg", c: "#e9e25a" },
      { l: "Trace", c: "#cfdd6a" },
      { l: "+", c: "#a9cf72" },
      { l: "++", c: "#79b97e" },
      { l: "+++", c: "#4f9e86" },
    ],
  },
  glucose: {
    name: "Glucose",
    levels: [
      { l: "Neg", c: "#5fb6c9" },
      { l: "Trace", c: "#6fae8e" },
      { l: "+", c: "#8aa85f" },
      { l: "++", c: "#b18a3e" },
      { l: "+++", c: "#8a5a2a" },
    ],
  },
  ketones: {
    name: "Ketones",
    levels: [
      { l: "Neg", c: "#f0d5a8" },
      { l: "Trace", c: "#e9b6a0" },
      { l: "Small", c: "#d98a8f" },
      { l: "Mod", c: "#c25b77" },
      { l: "Large", c: "#9e3b66" },
    ],
  },
  blood: {
    name: "Blood",
    levels: [
      { l: "Neg", c: "#f0c14b" },
      { l: "Trace", c: "#cdbb55" },
      { l: "+", c: "#8fae6a" },
      { l: "++", c: "#4f9a72" },
      { l: "+++", c: "#2f7d6a" },
    ],
  },
  bilirubin: {
    name: "Bilirubin",
    levels: [
      { l: "Neg", c: "#f3e7c8" },
      { l: "Small", c: "#e8c79a" },
      { l: "Mod", c: "#d8a06e" },
      { l: "Large", c: "#bd7a4e" },
    ],
  },
};

export const DIPSTICK_ORDER = ["pH", "protein", "glucose", "ketones", "blood", "bilirubin"];

// Read a refractometer USG against the true value: ±0.002 full, ±0.005 partial.
export const gradeUsg = (answer, usg) => {
  const a = parseFloat(answer);
  if (isNaN(a)) return { band: "wrong", pts: 0 };
  const d = Math.abs(a - usg);
  if (d <= 0.002) return { band: "correct", pts: 15 };
  if (d <= 0.005) return { band: "partial", pts: 8 };
  return { band: "wrong", pts: 0 };
};

// Grade the dipstick matches (scaled to 30). Exact level = 2, ±1 level = 1.
export const gradeDipstick = (rows, selected) => {
  let raw = 0;
  let correctCount = 0;
  rows.forEach((r) => {
    const sel = selected[r.key];
    if (sel == null) return;
    const diff = Math.abs(sel - r.correct);
    if (diff === 0) {
      raw += 2;
      correctCount += 1;
    } else if (diff === 1) raw += 1;
  });
  const maxRaw = rows.length * 2;
  return { pts: Math.round((30 * raw) / maxRaw), correctCount, total: rows.length };
};

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export const serveCase = (c) => ({
  ...c,
  gross: {
    ...c.gross,
    colorOptions: shuffle(c.gross.colorOptions),
    clarityOptions: shuffle(c.gross.clarityOptions),
  },
  refract: { ...c.refract, classifyOptions: shuffle(c.refract.classifyOptions) },
  diagnosis: { ...c.diagnosis, options: shuffle(c.diagnosis.options) },
});

export const URINALYSIS_CASES = [
  {
    id: "bailey-normal",
    name: "Bailey",
    species: "dog",
    breed: "Golden Retriever",
    signalment: "8 yr · male neutered",
    presentation: "Routine senior wellness screen; clinically well, free-catch sample.",
    gross: {
      colorKey: "yellow",
      turbidity: "clear",
      colorOptions: [
        { t: "Colourless / very pale", score: 0, note: "Too dilute-looking for this concentrated sample." },
        { t: "Pale to dark yellow", score: 3, note: "Normal yellow urine from urochrome pigment." },
        { t: "Orange-brown", score: 0, note: "Would suggest bilirubin." },
        { t: "Red / red-brown", score: 0, note: "Would suggest blood/haemoglobin." },
      ],
      clarityOptions: [
        { t: "Clear / transparent", score: 3, note: "Normal fresh urine is clear." },
        { t: "Cloudy", score: 0, note: "Implies cells, crystals or bacteria." },
        { t: "Turbid / flocculent", score: 0, note: "Heavy sediment — not seen here." },
      ],
    },
    refract: {
      usg: 1.04,
      classifyOptions: [
        { t: "Hypersthenuric / well concentrated (dog >1.030)", score: 3, note: "Good concentrating ability." },
        { t: "Minimally concentrated", score: 0, note: "USG is well above this." },
        { t: "Isosthenuric (1.008–1.012)", score: 0, note: "USG is far higher." },
        { t: "Hyposthenuric (<1.008)", score: 0, note: "Not dilute." },
      ],
    },
    dipstick: [
      { key: "pH", correct: 2 },
      { key: "protein", correct: 0 },
      { key: "glucose", correct: 0 },
      { key: "ketones", correct: 0 },
      { key: "blood", correct: 0 },
      { key: "bilirubin", correct: 1 },
    ],
    diagnosis: {
      prompt: "Pulling it together — what's the best interpretation?",
      options: [
        { t: "Normal urinalysis.", score: 3, note: "Concentrated, clear, chemistry essentially negative." },
        { t: "Bacterial urinary tract infection.", score: 0, note: "No pyuria, haematuria or alkaline pH." },
        { t: "Diabetes mellitus.", score: 0, note: "Glucose is negative." },
        { t: "Chronic kidney disease.", score: 0, note: "USG is well concentrated." },
      ],
      overview:
        "This is a normal canine urinalysis. A well-concentrated USG (>1.030 in the dog) shows the kidneys are concentrating appropriately. The chemistry is essentially negative. Note the trace bilirubin: small amounts can be normal in concentrated male-dog urine and aren't significant in isolation — but ANY bilirubinuria is abnormal in the cat. Always confirm with a sediment exam.",
    },
  },

  {
    id: "misty-diabetes",
    name: "Misty",
    species: "cat",
    breed: "Domestic Shorthair",
    signalment: "10 yr · female spayed",
    presentation: "PU/PD and weight loss despite a good appetite.",
    gross: {
      colorKey: "pale-yellow",
      turbidity: "clear",
      colorOptions: [
        { t: "Pale yellow", score: 3, note: "Dilute, pale urine from osmotic diuresis." },
        { t: "Dark amber", score: 0, note: "Too concentrated-looking for this dilute sample." },
        { t: "Red / red-brown", score: 0, note: "No blood seen." },
        { t: "Orange-brown", score: 0, note: "No bilirubin pigment." },
      ],
      clarityOptions: [
        { t: "Clear", score: 3, note: "No significant sediment." },
        { t: "Cloudy", score: 0, note: "Not cloudy here." },
        { t: "Turbid", score: 0, note: "No heavy sediment." },
      ],
    },
    refract: {
      usg: 1.025,
      classifyOptions: [
        { t: "Minimally concentrated (inappropriate for a dehydrated cat)", score: 3, note: "Osmotic diuresis from glucosuria lowers USG." },
        { t: "Well concentrated (cat >1.035)", score: 0, note: "USG is below the feline concentrating target." },
        { t: "Isosthenuric", score: 0, note: "Slightly above the isosthenuric band." },
        { t: "Hyposthenuric", score: 0, note: "Not that dilute." },
      ],
    },
    dipstick: [
      { key: "pH", correct: 1 },
      { key: "protein", correct: 1 },
      { key: "glucose", correct: 4 },
      { key: "ketones", correct: 3 },
      { key: "blood", correct: 0 },
      { key: "bilirubin", correct: 0 },
    ],
    diagnosis: {
      prompt: "Given the findings, what's the most likely diagnosis?",
      options: [
        { t: "Diabetes mellitus with ketonuria (developing ketoacidosis).", score: 3, note: "Marked glucosuria plus ketones in a PU/PD cat." },
        { t: "Bacterial cystitis.", score: 0, note: "No haematuria/pyuria or alkaline pH." },
        { t: "Stress glucosuria — ignore.", score: 1, note: "Stress hyperglycaemia in cats can spill glucose, but ketones make diabetes far more likely." },
        { t: "Normal urinalysis.", score: 0, note: "Glucosuria and ketonuria are clearly abnormal." },
      ],
      overview:
        "Glucose appears in urine once blood glucose exceeds the renal threshold (~10–12 mmol/L in cats). The osmotic pull of that glucose causes the polyuria/polydipsia and the lower USG. Ketones appear when insulin deficiency forces fat breakdown for energy — a warning sign of diabetic ketoacidosis (DKA), which is an emergency. Confirm persistent hyperglycaemia (blood glucose + fructosamine) and assess hydration/acid–base before treatment. In a stressed cat, transient glucosuria can occur, but concurrent ketonuria strongly supports true diabetes.",
    },
  },

  {
    id: "rocky-uti",
    name: "Rocky",
    species: "dog",
    breed: "Miniature Schnauzer",
    signalment: "6 yr · female spayed",
    presentation: "Straining to urinate, frequent small volumes, licking at the vulva.",
    gross: {
      colorKey: "yellow",
      turbidity: "turbid",
      colorOptions: [
        { t: "Yellow", score: 3, note: "Colour is yellow; the key abnormality is the turbidity." },
        { t: "Colourless", score: 0, note: "Not dilute-looking." },
        { t: "Red / red-brown", score: 0, note: "Frank blood isn't the dominant feature here." },
        { t: "Orange-brown", score: 0, note: "No bilirubin pigment." },
      ],
      clarityOptions: [
        { t: "Turbid / flocculent", score: 3, note: "Heavy sediment of cells, bacteria and crystals." },
        { t: "Clear", score: 0, note: "This sample is clearly not clear." },
        { t: "Slightly cloudy", score: 1, note: "It's more than slightly cloudy — frankly turbid." },
      ],
    },
    refract: {
      usg: 1.035,
      classifyOptions: [
        { t: "Concentrated (>1.030)", score: 3, note: "Concentrating ability is preserved." },
        { t: "Isosthenuric", score: 0, note: "USG is well above this." },
        { t: "Hyposthenuric", score: 0, note: "Not dilute." },
        { t: "Minimally concentrated", score: 0, note: "USG is above this band." },
      ],
    },
    dipstick: [
      { key: "pH", correct: 5 },
      { key: "protein", correct: 2 },
      { key: "glucose", correct: 0 },
      { key: "ketones", correct: 0 },
      { key: "blood", correct: 3 },
      { key: "bilirubin", correct: 0 },
    ],
    diagnosis: {
      prompt: "What's the most likely diagnosis?",
      options: [
        { t: "Bacterial urinary tract infection (cystitis).", score: 3, note: "Alkaline pH, haematuria, proteinuria and turbid urine with pyuria." },
        { t: "Diabetes mellitus.", score: 0, note: "Glucose is negative." },
        { t: "Chronic kidney disease.", score: 0, note: "USG is well concentrated." },
        { t: "Normal urinalysis.", score: 0, note: "Multiple abnormalities are present." },
      ],
      overview:
        "Turbid urine with positive blood and protein, plus an alkaline pH, fits a bacterial cystitis. Urease-producing bacteria (e.g. Staphylococcus, Proteus) split urea into ammonia, raising pH and predisposing to struvite crystals. A sediment exam should show white cells (pyuria), red cells and bacteria — note the dipstick leukocyte pad is unreliable in dogs and cats, so confirm pyuria microscopically. Recommend a sterile (cystocentesis) sample for culture and sensitivity before antibiotics.",
    },
  },

  {
    id: "tigger-ckd",
    name: "Tigger",
    species: "cat",
    breed: "Domestic Shorthair",
    signalment: "16 yr · male neutered",
    presentation: "Weight loss, PU/PD and a poor coat; previously found to be azotaemic.",
    gross: {
      colorKey: "colourless",
      turbidity: "clear",
      colorOptions: [
        { t: "Colourless / very pale", score: 3, note: "Dilute urine appears almost colourless." },
        { t: "Dark amber", score: 0, note: "The opposite of this dilute sample." },
        { t: "Red / red-brown", score: 0, note: "No blood." },
        { t: "Orange-brown", score: 0, note: "No bilirubin." },
      ],
      clarityOptions: [
        { t: "Clear", score: 3, note: "No significant sediment." },
        { t: "Cloudy", score: 0, note: "Not cloudy." },
        { t: "Turbid", score: 0, note: "No heavy sediment." },
      ],
    },
    refract: {
      usg: 1.011,
      classifyOptions: [
        { t: "Isosthenuric (1.008–1.012) — failure to concentrate", score: 3, note: "Urine osmolality equals plasma; the hallmark of renal failure when azotaemic." },
        { t: "Well concentrated", score: 0, note: "USG is very low." },
        { t: "Hypersthenuric", score: 0, note: "Definitely not concentrated." },
        { t: "Hyposthenuric (<1.008)", score: 1, note: "Close, but this value sits in the isosthenuric band." },
      ],
    },
    dipstick: [
      { key: "pH", correct: 2 },
      { key: "protein", correct: 2 },
      { key: "glucose", correct: 0 },
      { key: "ketones", correct: 0 },
      { key: "blood", correct: 0 },
      { key: "bilirubin", correct: 0 },
    ],
    diagnosis: {
      prompt: "What's the most likely diagnosis?",
      options: [
        { t: "Chronic kidney disease (loss of urine-concentrating ability).", score: 3, note: "Isosthenuria with proteinuria in an azotaemic older cat." },
        { t: "Diabetes mellitus.", score: 0, note: "Glucose is negative." },
        { t: "Bacterial cystitis.", score: 0, note: "No haematuria, pyuria or alkaline pH." },
        { t: "Normal urinalysis.", score: 0, note: "Isosthenuria in an azotaemic cat is abnormal." },
      ],
      overview:
        "An isosthenuric USG (1.008–1.012) means the kidneys are neither concentrating nor diluting — urine the same as plasma. In a patient that is azotaemic and clinically dehydrated, this confirms the azotaemia is renal in origin (the kidneys should be making concentrated urine to conserve water but can't). The mild proteinuria suggests glomerular/tubular damage — quantify it with a urine protein:creatinine ratio (UPC). Always interpret USG alongside hydration and blood results.",
    },
  },

  {
    id: "daisy-haematuria",
    name: "Daisy",
    species: "dog",
    breed: "Dalmatian",
    signalment: "5 yr · male neutered",
    presentation: "Visibly bloody urine and straining; previous urinary stones.",
    gross: {
      colorKey: "red-brown",
      turbidity: "turbid",
      colorOptions: [
        { t: "Red / red-brown", score: 3, note: "Frank blood discolours the urine." },
        { t: "Yellow", score: 0, note: "Clearly discoloured red, not yellow." },
        { t: "Orange-brown", score: 1, note: "There is a brown tinge, but red predominates." },
        { t: "Colourless", score: 0, note: "Not dilute/colourless." },
      ],
      clarityOptions: [
        { t: "Turbid", score: 3, note: "Cells and crystals make it turbid." },
        { t: "Clear", score: 0, note: "Not clear." },
        { t: "Cloudy", score: 1, note: "More than cloudy — frankly turbid with sediment." },
      ],
    },
    refract: {
      usg: 1.03,
      classifyOptions: [
        { t: "Concentrated (~1.030)", score: 3, note: "Concentrating ability preserved." },
        { t: "Isosthenuric", score: 0, note: "USG is higher." },
        { t: "Hyposthenuric", score: 0, note: "Not dilute." },
        { t: "Minimally concentrated", score: 1, note: "Borderline, but 1.030 is at the concentrated threshold." },
      ],
    },
    dipstick: [
      { key: "pH", correct: 4 },
      { key: "protein", correct: 2 },
      { key: "glucose", correct: 0 },
      { key: "ketones", correct: 0 },
      { key: "blood", correct: 4 },
      { key: "bilirubin", correct: 0 },
    ],
    diagnosis: {
      prompt: "What's the most likely interpretation?",
      options: [
        { t: "Haematuria from the lower urinary tract (e.g. uroliths/cystitis).", score: 3, note: "Red urine, strongly positive blood and turbidity with RBCs on sediment." },
        { t: "Haemoglobinuria from intravascular haemolysis.", score: 1, note: "Possible, but with stones and turbidity, true haematuria is more likely — spin the sample to tell them apart." },
        { t: "Diabetes mellitus.", score: 0, note: "Glucose is negative." },
        { t: "Normal urinalysis.", score: 0, note: "Frank blood is clearly abnormal." },
      ],
      overview:
        "A strongly positive blood pad has three causes: haematuria (intact red cells), haemoglobinuria (free haemoglobin from intravascular haemolysis) and myoglobinuria (muscle injury). Distinguish them by spinning the sample: in haematuria the red cells settle into a pellet and the supernatant clears, and RBCs are seen on sediment; in haemoglobinuria/myoglobinuria the supernatant stays discoloured. Here, with a history of stones, turbid red urine and proteinuria, true haematuria from urolithiasis/cystitis is most likely — examine the sediment for RBCs and crystals and image the bladder.",
    },
  },

  {
    id: "jasper-bilirubin",
    name: "Jasper",
    species: "dog",
    breed: "Cocker Spaniel",
    signalment: "9 yr · female spayed",
    presentation: "Off-colour, vomiting, and the owner has noticed unusually dark urine.",
    gross: {
      colorKey: "orange-brown",
      turbidity: "clear",
      colorOptions: [
        { t: "Orange-brown", score: 3, note: "Bilirubin pigment darkens the urine." },
        { t: "Yellow", score: 1, note: "Darker than normal yellow — distinctly orange-brown." },
        { t: "Red / red-brown", score: 0, note: "It's orange-brown rather than red." },
        { t: "Colourless", score: 0, note: "Far from colourless." },
      ],
      clarityOptions: [
        { t: "Clear", score: 3, note: "Pigment darkens it but it remains clear." },
        { t: "Turbid", score: 0, note: "No heavy sediment." },
        { t: "Cloudy", score: 0, note: "Not cloudy." },
      ],
    },
    refract: {
      usg: 1.035,
      classifyOptions: [
        { t: "Concentrated (>1.030)", score: 3, note: "Concentrating ability preserved." },
        { t: "Isosthenuric", score: 0, note: "USG is well above this." },
        { t: "Hyposthenuric", score: 0, note: "Not dilute." },
        { t: "Minimally concentrated", score: 0, note: "USG is above this band." },
      ],
    },
    dipstick: [
      { key: "pH", correct: 2 },
      { key: "protein", correct: 0 },
      { key: "glucose", correct: 0 },
      { key: "ketones", correct: 0 },
      { key: "blood", correct: 0 },
      { key: "bilirubin", correct: 3 },
    ],
    diagnosis: {
      prompt: "What's the most likely interpretation?",
      options: [
        { t: "Marked bilirubinuria — investigate hepatobiliary disease or haemolysis.", score: 3, note: "Dark orange urine with a large bilirubin reading is significant." },
        { t: "Normal — bilirubin is always fine in dogs.", score: 1, note: "Trace bilirubin can be normal in concentrated dog urine, but a LARGE reading with dark urine is abnormal." },
        { t: "Bacterial cystitis.", score: 0, note: "No haematuria, pyuria or alkaline pH." },
        { t: "Diabetes mellitus.", score: 0, note: "Glucose is negative." },
      ],
      overview:
        "Bilirubin in urine produces dark orange-brown discolouration. Small amounts can be normal in concentrated canine urine (dogs have a low renal threshold for bilirubin), but a LARGE reading like this is significant and often appears before the patient looks jaundiced. It points to increased bilirubin from hepatobiliary disease (cholestasis) or haemolysis. Importantly, ANY bilirubinuria in a cat is abnormal. Follow up with serum biochemistry (bilirubin, liver enzymes) and a PCV/blood smear to screen for haemolysis.",
    },
  },

  {
    id: "rosie-struvite",
    name: "Rosie",
    species: "dog",
    breed: "Shih Tzu",
    signalment: "7 yr · female spayed",
    presentation: "Recurrent straining and frequent small voids; a gritty sediment noticed in the sample pot.",
    gross: {
      colorKey: "yellow",
      turbidity: "turbid",
      colorOptions: [
        { t: "Yellow", score: 3, note: "Colour is yellow; the turbidity is the key feature." },
        { t: "Colourless", score: 0, note: "Not dilute-looking." },
        { t: "Red / red-brown", score: 0, note: "Frank blood isn't the dominant feature." },
        { t: "Orange-brown", score: 0, note: "No bilirubin pigment." },
      ],
      clarityOptions: [
        { t: "Turbid / flocculent", score: 3, note: "Heavy sediment of crystals, cells and bacteria." },
        { t: "Clear", score: 0, note: "Clearly not clear." },
        { t: "Slightly cloudy", score: 1, note: "More than slightly — frankly turbid." },
      ],
    },
    refract: {
      usg: 1.032,
      classifyOptions: [
        { t: "Concentrated (>1.030)", score: 3, note: "Concentrating ability preserved." },
        { t: "Isosthenuric", score: 0, note: "USG is well above this." },
        { t: "Hyposthenuric", score: 0, note: "Not dilute." },
        { t: "Minimally concentrated", score: 1, note: "Borderline, but 1.032 is concentrated." },
      ],
    },
    dipstick: [
      { key: "pH", correct: 6 },
      { key: "protein", correct: 2 },
      { key: "glucose", correct: 0 },
      { key: "ketones", correct: 0 },
      { key: "blood", correct: 3 },
      { key: "bilirubin", correct: 0 },
    ],
    diagnosis: {
      prompt: "What's the most likely interpretation?",
      options: [
        { t: "Struvite crystalluria with a urease-producing bacterial UTI (alkaline urine).", score: 3, note: "Markedly alkaline pH, haematuria, turbidity and gritty sediment fit struvite + infection." },
        { t: "Diabetes mellitus.", score: 0, note: "Glucose is negative." },
        { t: "Chronic kidney disease.", score: 0, note: "USG is well concentrated." },
        { t: "Normal urinalysis.", score: 0, note: "Alkaline pH with blood, protein and gritty sediment is abnormal." },
      ],
      overview:
        "Urease-producing bacteria (Staphylococcus, Proteus) split urea into ammonia, driving the pH strongly alkaline and favouring struvite (magnesium-ammonium-phosphate) crystal formation. A fresh sediment exam should reveal the classic 'coffin-lid' struvite crystals plus white cells, red cells and bacteria. Collect a cystocentesis sample for culture and sensitivity before antibiotics, and address the diet/stone management — struvite stones often dissolve with appropriate diet and infection control.",
    },
  },

  {
    id: "felix-fic",
    name: "Felix",
    species: "cat",
    breed: "British Shorthair",
    signalment: "4 yr · male neutered",
    presentation: "Indoor cat, recent house move; straining in the tray, frequent visits, small bloody voids and over-grooming.",
    gross: {
      colorKey: "red-brown",
      turbidity: "cloudy",
      colorOptions: [
        { t: "Red / red-brown", score: 3, note: "Blood discolours the small voids." },
        { t: "Yellow", score: 0, note: "Clearly discoloured." },
        { t: "Orange-brown", score: 1, note: "There's a brown tinge, but red predominates." },
        { t: "Colourless", score: 0, note: "Not dilute." },
      ],
      clarityOptions: [
        { t: "Cloudy", score: 3, note: "Red cells and a little debris make it cloudy." },
        { t: "Clear", score: 0, note: "Not clear." },
        { t: "Turbid / flocculent", score: 1, note: "Cloudy rather than heavily flocculent." },
      ],
    },
    refract: {
      usg: 1.048,
      classifyOptions: [
        { t: "Well concentrated (cat >1.035)", score: 3, note: "Excellent feline concentrating ability — rules out renal failure as the cause." },
        { t: "Minimally concentrated", score: 0, note: "USG is very high." },
        { t: "Isosthenuric", score: 0, note: "Far above this band." },
        { t: "Hyposthenuric", score: 0, note: "Not dilute at all." },
      ],
    },
    dipstick: [
      { key: "pH", correct: 2 },
      { key: "protein", correct: 1 },
      { key: "glucose", correct: 0 },
      { key: "ketones", correct: 0 },
      { key: "blood", correct: 3 },
      { key: "bilirubin", correct: 0 },
    ],
    diagnosis: {
      prompt: "What's the most likely interpretation?",
      options: [
        { t: "Feline idiopathic cystitis (FIC) — sterile, stress-associated lower urinary tract disease.", score: 3, note: "A young, stressed cat with haematuria, concentrated urine and NO infection/crystals fits FIC." },
        { t: "Bacterial urinary tract infection.", score: 1, note: "UTIs are uncommon in young cats with concentrated urine; FIC is far more likely, but culture excludes infection." },
        { t: "Chronic kidney disease.", score: 0, note: "USG is excellent (1.048) — concentrating ability is intact." },
        { t: "Diabetes mellitus.", score: 0, note: "Glucose is negative." },
      ],
      overview:
        "Feline idiopathic (interstitial) cystitis is the commonest cause of lower urinary signs in young-to-middle-aged cats and is strongly linked to stress (here, a house move). The urine is typically concentrated and STERILE — haematuria without bacteria or significant crystalluria. Management focuses on stress reduction and environmental enrichment (the MEMO approach: multimodal environmental modification), increased water intake and pain relief. Critically, always check a male cat is not obstructed — a blocked bladder is an emergency.",
    },
  },

  {
    id: "duke-diabetes-dog",
    name: "Duke",
    species: "dog",
    breed: "Beagle",
    signalment: "9 yr · male neutered",
    presentation: "Drinking and urinating much more, ravenous appetite yet losing weight.",
    gross: {
      colorKey: "pale-yellow",
      turbidity: "clear",
      colorOptions: [
        { t: "Pale yellow", score: 3, note: "Dilute, pale urine from osmotic diuresis." },
        { t: "Dark amber", score: 0, note: "Too concentrated-looking for this sample." },
        { t: "Red / red-brown", score: 0, note: "No blood." },
        { t: "Orange-brown", score: 0, note: "No bilirubin pigment." },
      ],
      clarityOptions: [
        { t: "Clear", score: 3, note: "No significant sediment." },
        { t: "Cloudy", score: 0, note: "Not cloudy." },
        { t: "Turbid", score: 0, note: "No heavy sediment." },
      ],
    },
    refract: {
      usg: 1.022,
      classifyOptions: [
        { t: "Minimally concentrated (osmotic diuresis from glucosuria)", score: 3, note: "Glucose pulls water into the urine, lowering USG." },
        { t: "Well concentrated (>1.030)", score: 0, note: "USG is below this." },
        { t: "Isosthenuric", score: 1, note: "Close to, but just above, the isosthenuric band." },
        { t: "Hyposthenuric", score: 0, note: "Not that dilute." },
      ],
    },
    dipstick: [
      { key: "pH", correct: 1 },
      { key: "protein", correct: 1 },
      { key: "glucose", correct: 4 },
      { key: "ketones", correct: 1 },
      { key: "blood", correct: 0 },
      { key: "bilirubin", correct: 0 },
    ],
    diagnosis: {
      prompt: "What's the most likely diagnosis?",
      options: [
        { t: "Diabetes mellitus (marked glucosuria, early ketones).", score: 3, note: "Glucosuria in a PU/PD, polyphagic dog losing weight is classic diabetes." },
        { t: "Renal glucosuria — the kidneys are normal otherwise.", score: 1, note: "Primary renal glucosuria exists but is rare; confirm blood glucose first." },
        { t: "Bacterial cystitis.", score: 0, note: "No alkaline pH, pyuria or haematuria." },
        { t: "Normal urinalysis.", score: 0, note: "Marked glucosuria is abnormal." },
      ],
      overview:
        "Glucose spills into urine once blood glucose exceeds the renal threshold (~10–12 mmol/L). The osmotic effect drives the PU/PD and lowers USG. Always confirm with a blood glucose — and in a dog the diagnosis is more straightforward than the cat (stress hyperglycaemia is far less of a confounder). The trace ketones are an early warning to check for diabetic ketoacidosis. Confirm with paired blood glucose/fructosamine and assess hydration and electrolytes before starting insulin under veterinary direction.",
    },
  },

  {
    id: "mittens-ckd-cat",
    name: "Mittens",
    species: "cat",
    breed: "Domestic Longhair",
    signalment: "17 yr · female spayed",
    presentation: "Long-stay PU/PD, weight loss, intermittent vomiting; mildly azotaemic on previous bloods.",
    gross: {
      colorKey: "pale-yellow",
      turbidity: "clear",
      colorOptions: [
        { t: "Pale yellow", score: 3, note: "Dilute, poorly-concentrated urine." },
        { t: "Dark amber", score: 0, note: "Opposite of this dilute sample." },
        { t: "Red / red-brown", score: 0, note: "No blood." },
        { t: "Orange-brown", score: 0, note: "No bilirubin." },
      ],
      clarityOptions: [
        { t: "Clear", score: 3, note: "No significant sediment." },
        { t: "Cloudy", score: 0, note: "Not cloudy." },
        { t: "Turbid", score: 0, note: "No heavy sediment." },
      ],
    },
    refract: {
      usg: 1.014,
      classifyOptions: [
        { t: "Minimally concentrated / borderline isosthenuric — inappropriate for an azotaemic cat", score: 3, note: "A cat should concentrate to >1.035; failing to do so when azotaemic indicates renal disease." },
        { t: "Well concentrated (>1.035)", score: 0, note: "USG is far below the feline target." },
        { t: "Hypersthenuric", score: 0, note: "Definitely not concentrated." },
        { t: "Hyposthenuric (<1.008)", score: 1, note: "Close, but this value is just above the isosthenuric band." },
      ],
    },
    dipstick: [
      { key: "pH", correct: 2 },
      { key: "protein", correct: 2 },
      { key: "glucose", correct: 0 },
      { key: "ketones", correct: 0 },
      { key: "blood", correct: 0 },
      { key: "bilirubin", correct: 0 },
    ],
    diagnosis: {
      prompt: "What's the most likely diagnosis?",
      options: [
        { t: "Chronic kidney disease — failure to concentrate urine with proteinuria in an azotaemic cat.", score: 3, note: "Poorly concentrated urine + proteinuria + azotaemia is classic feline CKD." },
        { t: "Diabetes mellitus.", score: 0, note: "Glucose is negative." },
        { t: "Bacterial cystitis.", score: 0, note: "No alkaline pH, haematuria or pyuria." },
        { t: "Normal urinalysis.", score: 0, note: "Poorly concentrated, proteinuric urine in an azotaemic cat is abnormal." },
      ],
      overview:
        "In a cat, a USG below ~1.035 alongside azotaemia indicates the kidneys can't conserve water as they should — supporting renal (rather than pre-renal) azotaemia. Quantify the proteinuria with a urine protein:creatinine ratio (UPC), measure blood pressure, and stage the disease (IRIS). Renal proteinuria is a negative prognostic marker and may warrant specific therapy. Always interpret USG together with hydration status and serum creatinine/SDMA.",
    },
  },

  {
    id: "bruno-haemoglobinuria",
    name: "Bruno",
    species: "dog",
    breed: "Cocker Spaniel",
    signalment: "5 yr · male neutered",
    presentation: "Sudden weakness, pale-then-yellow gums; the owner reports 'red wee' but no straining.",
    gross: {
      colorKey: "red-brown",
      turbidity: "clear",
      colorOptions: [
        { t: "Red / red-brown", score: 3, note: "Discoloured by pigment." },
        { t: "Yellow", score: 0, note: "Clearly discoloured." },
        { t: "Orange-brown", score: 1, note: "Brownish tinge, but predominantly red." },
        { t: "Colourless", score: 0, note: "Not dilute." },
      ],
      clarityOptions: [
        { t: "Clear", score: 3, note: "Uniformly red but CLEAR — a clue it isn't intact red cells settling out." },
        { t: "Turbid", score: 0, note: "No heavy sediment." },
        { t: "Cloudy", score: 1, note: "It stays clear; the colour is dissolved pigment." },
      ],
    },
    refract: {
      usg: 1.03,
      classifyOptions: [
        { t: "Concentrated (~1.030)", score: 3, note: "Concentrating ability preserved." },
        { t: "Isosthenuric", score: 0, note: "USG is higher." },
        { t: "Hyposthenuric", score: 0, note: "Not dilute." },
        { t: "Minimally concentrated", score: 1, note: "Borderline; 1.030 is at the concentrated threshold." },
      ],
    },
    dipstick: [
      { key: "pH", correct: 2 },
      { key: "protein", correct: 2 },
      { key: "glucose", correct: 0 },
      { key: "ketones", correct: 0 },
      { key: "blood", correct: 4 },
      { key: "bilirubin", correct: 2 },
    ],
    diagnosis: {
      prompt: "The blood pad is strongly positive but the urine is CLEAR after spinning. What's the most likely interpretation?",
      options: [
        { t: "Haemoglobinuria from intravascular haemolysis (e.g. IMHA) — not true haematuria.", score: 3, note: "A strongly positive blood pad with CLEAR, red supernatant (and no RBCs on sediment) points to free haemoglobin, not intact red cells." },
        { t: "Lower urinary tract haematuria from stones.", score: 1, note: "Possible, but with clear red urine that doesn't clear on spinning and signs of haemolysis, haemoglobinuria is more likely." },
        { t: "Diabetes mellitus.", score: 0, note: "Glucose is negative." },
        { t: "Normal urinalysis.", score: 0, note: "A strongly positive blood pad with bilirubin is abnormal." },
      ],
      overview:
        "The dipstick blood pad reacts to haematuria, haemoglobinuria AND myoglobinuria. To distinguish them, spin the sample: with haematuria (intact red cells) the supernatant clears and RBCs sit in the sediment; with haemoglobinuria/myoglobinuria the supernatant stays discoloured. Here, red but CLEAR urine plus jaundice, weakness and bilirubinuria point to intravascular haemolysis — check a PCV, blood smear (spherocytes/agglutination) and bilirubin urgently. This is a medical emergency, not a urinary-tract bleed.",
    },
  },

  {
    id: "biscuit-cushings",
    name: "Biscuit",
    species: "dog",
    breed: "Miniature Poodle",
    signalment: "11 yr · female spayed",
    presentation: "Marked PU/PD, a pot-bellied appearance, thinning coat and a big appetite.",
    gross: {
      colorKey: "colourless",
      turbidity: "clear",
      colorOptions: [
        { t: "Colourless / very pale", score: 3, note: "Very dilute urine." },
        { t: "Dark amber", score: 0, note: "Opposite of this dilute sample." },
        { t: "Red / red-brown", score: 0, note: "No blood." },
        { t: "Orange-brown", score: 0, note: "No bilirubin." },
      ],
      clarityOptions: [
        { t: "Clear", score: 3, note: "No significant sediment." },
        { t: "Cloudy", score: 1, note: "Cushingoid dogs are prone to UTIs, but this sample is clear." },
        { t: "Turbid", score: 0, note: "No heavy sediment." },
      ],
    },
    refract: {
      usg: 1.008,
      classifyOptions: [
        { t: "Hyposthenuric / very dilute (<1.008–1.012)", score: 3, note: "Marked dilution from PU/PD; the kidneys are diluting urine well below plasma." },
        { t: "Well concentrated", score: 0, note: "USG is very low." },
        { t: "Hypersthenuric", score: 0, note: "Definitely not concentrated." },
        { t: "Concentrated (>1.030)", score: 0, note: "Far from concentrated." },
      ],
    },
    dipstick: [
      { key: "pH", correct: 2 },
      { key: "protein", correct: 1 },
      { key: "glucose", correct: 0 },
      { key: "ketones", correct: 0 },
      { key: "blood", correct: 0 },
      { key: "bilirubin", correct: 0 },
    ],
    diagnosis: {
      prompt: "What's the best interpretation?",
      options: [
        { t: "Very dilute urine in a PU/PD dog with Cushingoid signs — investigate hyperadrenocorticism (and rule out other PU/PD causes).", score: 3, note: "Hyposthenuria plus a pot-belly, alopecia and polyphagia fits Cushing's; glucose is negative so it isn't diabetes." },
        { t: "Diabetes mellitus.", score: 0, note: "Glucose is negative." },
        { t: "Chronic kidney disease with isosthenuria.", score: 1, note: "Possible differential, but the very low USG with classic Cushingoid signs favours hyperadrenocorticism; confirm with bloods/screening tests." },
        { t: "Normal urinalysis.", score: 0, note: "Marked hyposthenuria with this clinical picture is abnormal." },
      ],
      overview:
        "Hyperadrenocorticism (Cushing's) impairs the action of antidiuretic hormone, producing marked PU/PD and very dilute (hyposthenuric) urine. Glucose is typically negative (helping separate it from diabetes), though concurrent diabetes can occur. The diagnosis is confirmed with screening tests (ACTH stimulation or low-dose dexamethasone suppression) plus supportive bloods (raised ALP). Note these dogs are prone to (often silent) UTIs, so a cystocentesis culture is worthwhile even when the sediment looks bland.",
    },
  },

  {
    id: "shadow-glomerular",
    name: "Shadow",
    species: "dog",
    breed: "Soft-Coated Wheaten Terrier",
    signalment: "8 yr · male neutered",
    presentation: "Mildly off-colour with some limb oedema; found to have low albumin on a screen.",
    gross: {
      colorKey: "yellow",
      turbidity: "clear",
      colorOptions: [
        { t: "Yellow", score: 3, note: "Normal yellow colour." },
        { t: "Colourless", score: 0, note: "Not especially dilute-looking." },
        { t: "Red / red-brown", score: 0, note: "No blood." },
        { t: "Orange-brown", score: 0, note: "No bilirubin." },
      ],
      clarityOptions: [
        { t: "Clear", score: 3, note: "No significant sediment — the proteinuria isn't from infection/inflammation." },
        { t: "Cloudy", score: 0, note: "Not cloudy." },
        { t: "Turbid", score: 0, note: "No heavy sediment." },
      ],
    },
    refract: {
      usg: 1.025,
      classifyOptions: [
        { t: "Minimally concentrated", score: 3, note: "Reasonably concentrated, so the heavy proteinuria isn't simply dilution." },
        { t: "Hyposthenuric", score: 0, note: "USG is higher than this." },
        { t: "Isosthenuric", score: 1, note: "Just above the isosthenuric band." },
        { t: "Well concentrated (>1.030)", score: 0, note: "Slightly below this." },
      ],
    },
    dipstick: [
      { key: "pH", correct: 2 },
      { key: "protein", correct: 4 },
      { key: "glucose", correct: 0 },
      { key: "ketones", correct: 0 },
      { key: "blood", correct: 0 },
      { key: "bilirubin", correct: 0 },
    ],
    diagnosis: {
      prompt: "Marked proteinuria (+++) with a bland sediment. What's the most likely interpretation?",
      options: [
        { t: "Glomerular (renal) protein loss — quantify with a UPC ratio; consider protein-losing nephropathy.", score: 3, note: "Heavy proteinuria with an inactive sediment and concentrated urine points to glomerular loss, not infection." },
        { t: "Bacterial cystitis causing the protein.", score: 1, note: "Inflammation can add protein, but the sediment is bland and the protein is heavy — think glomerular." },
        { t: "Diabetes mellitus.", score: 0, note: "Glucose is negative." },
        { t: "Normal — protein is always fine in dogs.", score: 0, note: "Persistent +++ proteinuria is significant." },
      ],
      overview:
        "Proteinuria must always be interpreted alongside the sediment and USG. Heavy protein with an INACTIVE sediment (no blood, pyuria or bacteria) in reasonably concentrated urine suggests glomerular disease — protein leaking through a damaged filtration barrier. Confirm and quantify with a urine protein:creatinine ratio (UPC) on a sediment-free sample, check albumin and blood pressure, and screen for underlying causes (infection, immune-mediated, neoplasia). With low albumin and oedema this fits a protein-losing nephropathy / early nephrotic picture.",
    },
  },

  {
    id: "ziggy-bilirubin-cat",
    name: "Ziggy",
    species: "cat",
    breed: "Domestic Shorthair",
    signalment: "12 yr · male neutered",
    presentation: "Anorexic for several days, jaundiced sclera; suspected hepatic lipidosis.",
    gross: {
      colorKey: "orange-brown",
      turbidity: "clear",
      colorOptions: [
        { t: "Orange-brown", score: 3, note: "Bilirubin pigment darkens the urine." },
        { t: "Yellow", score: 1, note: "Darker than normal — distinctly orange-brown." },
        { t: "Red / red-brown", score: 0, note: "It's orange-brown, not red." },
        { t: "Colourless", score: 0, note: "Far from colourless." },
      ],
      clarityOptions: [
        { t: "Clear", score: 3, note: "Pigmented but clear." },
        { t: "Turbid", score: 0, note: "No heavy sediment." },
        { t: "Cloudy", score: 0, note: "Not cloudy." },
      ],
    },
    refract: {
      usg: 1.04,
      classifyOptions: [
        { t: "Well concentrated (cat >1.035)", score: 3, note: "Good feline concentrating ability." },
        { t: "Minimally concentrated", score: 0, note: "USG is high." },
        { t: "Isosthenuric", score: 0, note: "Far above this band." },
        { t: "Hyposthenuric", score: 0, note: "Not dilute." },
      ],
    },
    dipstick: [
      { key: "pH", correct: 2 },
      { key: "protein", correct: 0 },
      { key: "glucose", correct: 0 },
      { key: "ketones", correct: 0 },
      { key: "blood", correct: 0 },
      { key: "bilirubin", correct: 3 },
    ],
    diagnosis: {
      prompt: "What's the key point about this result in a CAT?",
      options: [
        { t: "ANY bilirubinuria is abnormal in a cat — investigate hepatobiliary disease / cholestasis urgently.", score: 3, note: "Unlike dogs, cats have a high renal threshold for bilirubin, so any bilirubin in the urine is significant." },
        { t: "Trace bilirubin is normal in cats, as in dogs.", score: 0, note: "Incorrect — bilirubinuria is normal in some concentrated DOG urine but never in cats." },
        { t: "Bacterial cystitis.", score: 0, note: "No haematuria, pyuria or alkaline pH." },
        { t: "Diabetes mellitus.", score: 0, note: "Glucose is negative." },
      ],
      overview:
        "Cats have a much higher renal threshold for bilirubin than dogs, so ANY bilirubinuria in a cat is abnormal and warrants prompt investigation of hepatobiliary disease or haemolysis. In an anorexic, jaundiced cat this is highly consistent with cholestatic liver disease such as hepatic lipidosis or cholangitis. Follow up immediately with serum biochemistry (bilirubin, ALT, ALP, bile acids), a PCV/blood smear to exclude haemolysis, and abdominal imaging. Nutritional support is often a cornerstone of feline lipidosis management.",
    },
  },

  {
    id: "rocky-postrenal",
    name: "Rocky",
    species: "cat",
    breed: "Domestic Shorthair",
    signalment: "3 yr · male neutered",
    presentation: "Repeated unproductive trips to the tray, vocalising, a firm painful bladder — possible obstruction relieved by catheter; this is the post-relief sample.",
    gross: {
      colorKey: "red-brown",
      turbidity: "turbid",
      colorOptions: [
        { t: "Red / red-brown", score: 3, note: "Blood from the inflamed, recently-obstructed bladder/urethra." },
        { t: "Yellow", score: 0, note: "Clearly discoloured." },
        { t: "Orange-brown", score: 1, note: "Some brown tinge, but red predominates." },
        { t: "Colourless", score: 0, note: "Not dilute." },
      ],
      clarityOptions: [
        { t: "Turbid", score: 3, note: "Crystals, cells and debris — typical of obstructive disease." },
        { t: "Clear", score: 0, note: "Not clear." },
        { t: "Cloudy", score: 1, note: "More than cloudy — frankly turbid." },
      ],
    },
    refract: {
      usg: 1.04,
      classifyOptions: [
        { t: "Well concentrated (>1.035)", score: 3, note: "Concentrating ability preserved (helps separate post-renal from intrinsic renal failure)." },
        { t: "Isosthenuric", score: 0, note: "USG is far higher." },
        { t: "Hyposthenuric", score: 0, note: "Not dilute." },
        { t: "Minimally concentrated", score: 0, note: "USG is well above this." },
      ],
    },
    dipstick: [
      { key: "pH", correct: 4 },
      { key: "protein", correct: 2 },
      { key: "glucose", correct: 0 },
      { key: "ketones", correct: 0 },
      { key: "blood", correct: 4 },
      { key: "bilirubin", correct: 0 },
    ],
    diagnosis: {
      prompt: "What's the priority interpretation in this blocked tom cat?",
      options: [
        { t: "Obstructive feline lower urinary tract disease (FLUTD) with haematuria and crystalluria — a relieved urethral obstruction is an emergency requiring monitoring of potassium and kidney values.", score: 3, note: "A blocked male cat is life-threatening; the urine shows blood and crystals, but the systemic risks (hyperkalaemia, post-obstructive diuresis) matter most." },
        { t: "Simple cystitis — send home on the day.", score: 0, note: "A recently obstructed cat needs hospitalisation and monitoring, not immediate discharge." },
        { t: "Chronic kidney disease.", score: 0, note: "USG is excellent — concentrating ability is intact." },
        { t: "Diabetes mellitus.", score: 0, note: "Glucose is negative." },
      ],
      overview:
        "A blocked male cat is a true emergency. Even after the obstruction is relieved, the patient needs close monitoring: hyperkalaemia can cause fatal bradyarrhythmias, and a post-obstructive diuresis can cause dehydration and electrolyte swings. The urinalysis here shows haematuria and crystalluria typical of obstructive FLUTD. Examine the sediment for crystal type, monitor blood potassium and renal values, maintain the urinary catheter and IV fluids as directed, and manage pain. Diet and stress management help prevent recurrence.",
    },
  },

  {
    id: "lola-ketones",
    name: "Lola",
    species: "dog",
    breed: "Dachshund",
    signalment: "10 yr · female spayed",
    presentation: "Known diabetic, now anorexic, vomiting, lethargic and dehydrated; the owner skipped today's insulin.",
    gross: {
      colorKey: "pale-yellow",
      turbidity: "clear",
      colorOptions: [
        { t: "Pale yellow", score: 3, note: "Dilute from osmotic diuresis." },
        { t: "Dark amber", score: 0, note: "Too concentrated for this sample." },
        { t: "Red / red-brown", score: 0, note: "No blood." },
        { t: "Orange-brown", score: 0, note: "No bilirubin." },
      ],
      clarityOptions: [
        { t: "Clear", score: 3, note: "No significant sediment." },
        { t: "Cloudy", score: 0, note: "Not cloudy." },
        { t: "Turbid", score: 0, note: "No heavy sediment." },
      ],
    },
    refract: {
      usg: 1.02,
      classifyOptions: [
        { t: "Minimally concentrated (osmotic diuresis)", score: 3, note: "Glucosuria draws water into the urine." },
        { t: "Well concentrated (>1.030)", score: 0, note: "USG is below this." },
        { t: "Isosthenuric", score: 1, note: "Just above the isosthenuric band." },
        { t: "Hyposthenuric", score: 0, note: "Not that dilute." },
      ],
    },
    dipstick: [
      { key: "pH", correct: 1 },
      { key: "protein", correct: 1 },
      { key: "glucose", correct: 4 },
      { key: "ketones", correct: 4 },
      { key: "blood", correct: 0 },
      { key: "bilirubin", correct: 0 },
    ],
    diagnosis: {
      prompt: "What does this urinalysis indicate in a sick, vomiting diabetic?",
      options: [
        { t: "Diabetic ketoacidosis (DKA) — heavy glucosuria with LARGE ketones in a collapsing diabetic is an emergency.", score: 3, note: "Marked glucosuria plus large ketonuria in an unwell diabetic that missed insulin is DKA until proven otherwise." },
        { t: "Stable, well-controlled diabetes.", score: 0, note: "Large ketones with vomiting and dehydration is the opposite of stable." },
        { t: "Starvation ketosis only — give food and send home.", score: 0, note: "This is a metabolic emergency needing IV fluids, insulin and electrolyte correction." },
        { t: "Bacterial cystitis.", score: 0, note: "No haematuria, pyuria or alkaline pH." },
      ],
      overview:
        "Diabetic ketoacidosis develops when insulin deficiency forces the body to break down fat for energy, producing ketones and a metabolic acidosis. Heavy glucosuria with LARGE ketonuria in a vomiting, dehydrated diabetic is a medical emergency. Management is intensive: IV fluid resuscitation, careful electrolyte correction (especially potassium and phosphate), short-acting insulin under close monitoring, and treating any precipitating illness. Alert the vet immediately and prepare for hospitalisation and frequent monitoring.",
    },
  },
];
